<template>
    <div class="setup-container">
        <div class="header-search">
            <button class="setup-btn" @click="$router.push('/create-tournament')">
                <Plus class="icon" />
                <p style="padding: 0%; margin: 0%;">Setup</p>
            </button>
            <div class="search-bar">
                <Search />
                <input 
                    type="text" 
                    placeholder="Search by category, name, or title..." 
                    v-model="searchQuery" 
                    @input="handleSearch"
                />
            </div>
        </div>

        <!-- Search Results Info -->
        <div v-if="searchQuery" class="search-info">
            <p>Showing {{ filteredData.length }} results for "{{ searchQuery }}"</p>
            <button @click="clearSearch" class="clear-search-btn">
                Clear search
            </button>
        </div>

        <!-- Table -->
        <table class="table">
            <thead>
                <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Club</th>
                    <th>Location</th>
                    <th>Start Date<br> End Date</th>
                    <th>Reg. Deadline</th>
                    <th>Type</th>
                    <th>Category</th>
                    <th>Age Limits</th>
                    <th>Slot</th>
                    <th>Entry Fee</th>
                    <th>Action</th>
                </tr>
            </thead>
            <tbody>
                <tr v-if="paginatedData.length === 0">
                    <td colspan="12" class="no-data">
                        {{ searchQuery ? 'No results found' : 'No data available' }}
                    </td>
                </tr>
                <tr v-else v-for="(item, index) in paginatedData" :key="item.id">
                    <td>{{ (currentPage - 1) * pageSize + index + 1 }}</td>
                    <td>{{ item.name }}</td>
                    <td>{{ item.club_name }}</td>
                    <td>{{ item.location }}</td>
                    <td>{{ new Date(item.start_date).toDateString() }} - {{ new Date(item.end_date).toDateString() }}</td>
                    <td>{{ new Date(item.registration_deadline).toDateString() }}</td>
                    <td>{{ item.type }}</td>
                    <td>{{ item.category }}</td>
                    <td>{{ item.age_limits }}</td>
                    <td>{{ item.slot }}</td>
                    <td>{{ item.entry_fee }}</td>
                    <td class="actions">
                        <button @click="editItem(item)" class="btn edit">
                            <PenLine class="icon" />
                        </button>
                        <button @click="deleteItem(item)" class="btn delete">
                            <Trash class="icon" />
                        </button>
                    </td>
                </tr>
            </tbody>
        </table>

        <!-- Pagination -->
        <div class="pagination">
            <button @click="prevPage" :disabled="currentPage === 1">
                <ChevronLeft />
            </button>
            <button v-for="page in totalPages" :key="page" @click="goToPage(page)"
                :class="{ active: currentPage === page }">
                {{ page }}
            </button>
            <button @click="nextPage" :disabled="currentPage === totalPages">
                <ChevronRight />
            </button>
        </div>
    </div>
</template>

<script setup>
import { ChevronLeft, ChevronRight, PenLine, Plus, Search, Trash } from "lucide-vue-next";
import { ref, watch, computed, onMounted, onBeforeUnmount } from "vue";
import axios from 'axios';
import router from "@/router";

const dataList = ref([]);
const searchQuery = ref('');

// Pagination state
const currentPage = ref(1);
const pageSize = 8;

onMounted( async () => {
  await listRecords();
  
});

onBeforeUnmount( async () => {
  
});

// Computed values
const totalPages = computed(() =>
    Math.ceil(dataList.value.length / pageSize)
);

const paginatedData = computed(() => {
  const start = (currentPage.value - 1) * pageSize;
  return filteredData.value.slice(start, start + pageSize);
});
watch(searchQuery, () => {
  currentPage.value = 1;
});


const filteredData = computed(() => {
  if (!searchQuery.value) {
    return dataList.value;
  }
  
  const query = searchQuery.value.toLowerCase().trim();
  
  return dataList.value.filter(item => {
    return (
      (item.category && item.category.toLowerCase().includes(query)) ||
      (item.type && item.type.toLowerCase().includes(query)) ||
      (item.title && item.title.toLowerCase().includes(query))
    );
  });
});

// Pagination methods
function goToPage(page) {
    if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page;
    }
}

function prevPage() {
    if (currentPage.value > 1) {
        currentPage.value--;
    }
}

function nextPage() {
    if (currentPage.value < totalPages.value) {
        currentPage.value++;
    }
}

// Action methods
function clearSearch() {
  searchQuery.value = '';
  currentPage.value = 1;
}
const listRecords = async () => {
  try {
    const response = await axios.get('/tournament_lists.php', { });
    if (response.data.status) dataList.value = response.data.data;
    else console.error(response.data.message);
  } catch (error) {
    console.error(error.message);
  }
};


function editItem(item) {
  router.push(`/edit-tournament/${item.id}`);
}

const deleteItem = async (item) => {
  if (confirm(`Are you sure you want to delete ${item.name}?`)) {
    try {
      const response = await axios.get(`/tournament_delete.php?id=${item.id}`);
      if (response.data.status) {
        alert(response.data.message);
        // Remove from local data
        dataList.value = dataList.value.filter(i => i.id !== item.id);
      } else {
        console.error(response.data.message);
      }
    } catch (error) {
      console.error('Delete failed:', error.message);
    }
  }
};
</script>

<style scoped>
.setup-container {
    min-height: 100vh;
    padding: 24px;

}

/* Header: Setup button + search bar */
.header-search {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    width: 100%;
    margin-bottom: 16px;
}

/* Search bar */
.search-bar {
    display: flex;
    align-items: center;
    gap: 8px;
    border: 1px solid #CED4DA;
    border-radius: 8px;
    padding: 8px 12px;
    flex: 1 1 200px;
    /* allows shrinking */
    max-width: 350px;
}

.search-bar input {
    border: none;
    outline: none;
    width: 100%;
    font-size: 16px;
}

/* Table styles */
.table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 16px;
    border-radius: 8px;
    overflow-x: auto;
}

.table thead {
    display: table-header-group;
}

.table tbody {
    display: table-row-group;
}

.table th,
.table td {
    border: 1px solid #E9ECEF;
    border-radius: 8px;
    box-shadow: #6C757D33 0px 1px 3px 0px, #6C757D1A 0px 1px 2px 0px;
    padding: 12px 16px;
    text-align: left;
    font-size: 14px;
}

.table th {
    background: #fff;
    color: #6C757D;
}

.actions {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
}

.setup-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    background-color: #fff;
    color: #DB6813;
    border: 1px solid #DB6813;
    border-radius: 8px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
}

/* Buttons */
.btn {
    width: 36px;
    height: 36px;
    border: 1px solid #CED4DA;
    border-radius: 8px;
    color: #343A40;
    background: white;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0;
}

.btn:hover {
    border: 1px solid #0DCAF0;
    color: #0DCAF0;
}

.btn .icon {
    width: 18px;
    height: 18px;
}

/* Pagination */
.pagination {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 12px;
}

.pagination button {
    width: 36px;
    height: 36px;
    border: 1px solid #CED4DA;
    border-radius: 8px;
    color: #343A40;
    background: white;
    cursor: pointer;
}

.pagination button.active {
    border: 1px solid #0057FC;
    color: #0057FC;
}

.pagination button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background-color: #E9ECEF;
}

/* Mobile responsiveness */
@media (max-width: 768px) {
    .setup-container {
        padding: 12px;
    }

    .table th,
    .table td {
        padding: 8px 10px;
        font-size: 12px;
    }

    .search-bar {
        flex: 1 1 100%;
        margin-top: 8px;
    }

    .setup-btn {
        padding: 6px 10px;
        font-size: 12px;
    }

    .btn {
        width: 32px;
        height: 32px;
    }

    .btn .icon {
        width: 16px;
        height: 16px;
    }

    .pagination button {
        width: 32px;
        height: 32px;
        font-size: 12px;
    }

    /* Make table scrollable on mobile */
    .table {
        display: block;
        overflow-x: auto;
        white-space: nowrap;
    }
}
</style>
